'use client';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Document } from '@contentful/rich-text-types';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { Link } from '@/lib/i18n/navigation';

import { Icon } from '@/components/ui/Icon';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { EVENT_NAME } from '@/lib/gtm/const';

import styles from './Warranty.module.scss';

/**
 * Warranty text content from Contentful
 * Matches old solution's ContentfulPlaceholderFragment['warrantyText']
 */
export interface WarrantyTextContent {
  placeholderWarrantyHeading?: { text: string | null } | null;
  placeholderWarrantyText?: { json: unknown } | null;
  placeholderWarrantyText2?: { json: unknown } | null;
  url?: string | null;
  urlText?: string | null;
}

/**
 * "Taking Care of Your Watch" content from Contentful
 * Matches old solution's ContentfulPlaceholderFragment['warrantyTakingCareOfYourWatch']
 */
export interface WarrantyTakingCareContent {
  media?: {
    // CMSMedia structure from adapter
    url?: string | null;
    alt?: string | null;
    title?: string | null;
    // Legacy structure (raw from GraphQL)
    image?: { url: string | null } | null;
  } | null;
  placeholderWarrantyHeading?: { text: string | null } | null;
  placeholderWarrantyText?: { json: unknown } | null;
  placeholderWarrantyText2?: { json: unknown } | null;
  url?: string | null;
  urlText?: string | null;
}

export interface WarrantyProps {
  /** Section heading - from CMS or fallback */
  heading?: string;
  /** Warranty duration in years (e.g., 5) */
  warrantyDuration?: number;
  /** Extended warranty duration in years (e.g., 8) */
  warrantyExtDuration?: number;
  /** Rich text content for warranty terms from Contentful */
  warrantyText?: WarrantyTextContent | null;
  /** Rich text content for "Taking Care of Your Watch" from Contentful */
  warrantyTakingCareOfYourWatch?: WarrantyTakingCareContent | null;
  /** Whether movement is manufacture (for extension text variation) */
  movementManufacture?: string;
  /** Subtext rich text from CMS */
  subtext?: { json: unknown } | null;
  className?: string;
}

/**
 * Safely render Contentful rich text JSON
 */
const renderRichText = (json: unknown) => {
  if (!json) return null;
  try {
    return documentToReactComponents(json as Document);
  } catch {
    return null;
  }
};

export const Warranty = ({
  heading,
  warrantyDuration = 5,
  warrantyExtDuration = 8,
  warrantyText,
  warrantyTakingCareOfYourWatch,
  movementManufacture,
  subtext,
  className,
}: WarrantyProps) => {
  const t = useTranslations('product.warranty');
  const { sendIntentionEvent } = useGoogleAnalytics();
  const showManufacture = movementManufacture?.toLowerCase().includes('yes');

  const handleWarrantyExtensionClick = () => {
    sendIntentionEvent(EVENT_NAME.INTENTION_WARRANTY_EXTENSION);
  };

  // Get care image URL (from media.image.url or media.url)
  const careImageUrl =
    warrantyTakingCareOfYourWatch?.media?.image?.url ||
    warrantyTakingCareOfYourWatch?.media?.url ||
    null;

  return (
    <section className={clsx(styles.section, className)}>
      <div className={styles.container}>
        {/* Section Header - matches old SectionHeader */}
        <h2 className={styles.heading}>{heading || t('title')}</h2>

        <div className={styles.content}>
          {/* Left side - Warranty Details (38% on desktop) */}
          <div className={styles.leftColumn}>
            {/* Description wrapper with SVG badge */}
            <div className={styles.descriptionWrapper}>
              {/* Subtext / Description */}
              <div className={styles.description}>
                {subtext?.json ? renderRichText(subtext.json) : <p>{t('defaultDescription')}</p>}
              </div>

              {/* Warranty Badge with SVG and overlaid number */}
              <div className={styles.warrantyWrapper}>
                <span className={styles.warrantyDuration}>{warrantyDuration}</span>
                <Image
                  src="/images/warranty-duration.svg"
                  alt={`${warrantyDuration} ${t('years')}`}
                  width={97}
                  height={98}
                  className={styles.warrantyBadgeSvg}
                />
              </div>
            </div>

            {/* Spacer for flexbox alignment on desktop */}
            <div className={styles.flexSpace} />

            {/* Warranty Duration Line */}
            <p className={styles.duration}>
              {warrantyText?.placeholderWarrantyHeading?.text || t('durationLabel')}
              {': '}
              <span className={styles.durationValue}>{warrantyDuration}</span>
            </p>

            {/* Terms - rich text from CMS */}
            <div className={styles.terms}>
              {warrantyText?.placeholderWarrantyText?.json ? (
                renderRichText(warrantyText.placeholderWarrantyText.json)
              ) : (
                <p>{t('termsText')}</p>
              )}
            </div>

            {/* Extension Section */}
            <div className={styles.extensionBox}>
              <div className={styles.extensionDetails}>
                <span className={styles.extensionYears}>
                  {showManufacture
                    ? t('extensionYearsMechanical', { years: warrantyExtDuration })
                    : t('extensionYearsOther', { years: warrantyExtDuration })}
                </span>
                <span className={styles.extensionInfo}>
                  {showManufacture ? t('mechanicalMovements') : t('otherMovements')}
                </span>
              </div>

              {/* Extension description - rich text from CMS */}
              <div className={styles.extensionDescription}>
                {warrantyText?.placeholderWarrantyText2?.json ? (
                  renderRichText(warrantyText.placeholderWarrantyText2.json)
                ) : (
                  <p>{t('extensionDescription')}</p>
                )}
              </div>

              <Link
                href={warrantyText?.url || '/service/warranty-extension'}
                className={styles.extensionLink}
                onClick={handleWarrantyExtensionClick}
              >
                <span>{warrantyText?.urlText || t('extendCta')}</span>
                <Icon name="chevron-right" size={14} />
              </Link>
            </div>
          </div>

          {/* Right side - Taking Care of Your Watch (62% on desktop) */}
          <div className={styles.rightColumn}>
            <div className={styles.gridWrapper}>
              {/* Care Image */}
              {careImageUrl && (
                <div className={styles.careImageWrapper}>
                  <Image
                    src={careImageUrl}
                    alt={warrantyTakingCareOfYourWatch?.media?.alt || t('careTitle')}
                    fill
                    className={styles.careImage}
                  />
                </div>
              )}

              {/* Header */}
              <h3 className={styles.careHeading}>
                {warrantyTakingCareOfYourWatch?.placeholderWarrantyHeading?.text || t('careTitle')}
              </h3>

              {/* Description Top */}
              <div className={styles.careDescriptionTop}>
                {warrantyTakingCareOfYourWatch?.placeholderWarrantyText?.json ? (
                  renderRichText(warrantyTakingCareOfYourWatch.placeholderWarrantyText.json)
                ) : (
                  <p>{t('careDescription')}</p>
                )}
              </div>

              {/* Description Bottom (desktop only) */}
              <div className={styles.careDescriptionBottom}>
                {warrantyTakingCareOfYourWatch?.placeholderWarrantyText2?.json
                  ? renderRichText(warrantyTakingCareOfYourWatch.placeholderWarrantyText2.json)
                  : null}
              </div>

              {/* Explore Link */}
              <div className={styles.exploreLinkWrapper}>
                <Link
                  href={warrantyTakingCareOfYourWatch?.url || '/service/watches'}
                  className={styles.exploreLink}
                >
                  <span>{warrantyTakingCareOfYourWatch?.urlText || t('exploreCta')}</span>
                  <Icon name="chevron-right" size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Warranty;
